import React, { useEffect, useState } from "react";
import { Select } from "antd";
import { API_ENDPOINTS } from "config/config";
import axios from "axios";
import Toast from "components/ui/tableComponents/Toast";
import { useSettings } from "context/GlobalSettingContext";
import useHttp from "hooks/useHttp";
import StripeProductTrackingPanel from "./StripeProductTrackingPanel";

export default function SettingsData() {
    const { settings, setSettings } = useSettings();
    const api = useHttp();
    const [loading, setLoading] = useState(false);
    const [settingsId, setSettingsId] = useState("");
    const [isActive, setIsActive] = useState(false);
    const [firstDropdown, setFirstDropdown] = useState("");
    const [secondDropdown, setSecondDropdown] = useState("");
    const [toast, setToast] = useState({ show: false, message: "", type: "" });

    const token = localStorage.getItem("authToken");
    const { Option } = Select;

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const res = await axios.get(API_ENDPOINTS.GET_SETTINGS, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res?.data?.data) {
                const { _id, isShowDeletedRecords, salesRecordBy, expenseRecordBy } = res.data.data;
                setSettingsId(_id || "");
                setIsActive(!!isShowDeletedRecords);
                setFirstDropdown(salesRecordBy || "");
                setSecondDropdown(expenseRecordBy || "");
            }
        } catch (err) {
            console.error(err);
            setToast({ show: true, message: "Failed to fetch settings", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    const handleStatusToggle = () => setIsActive((prev) => !prev);

    const handleSave = async () => {
        if (!settingsId) {
            setToast({ show: true, message: "Settings ID not found", type: "error" });
            return;
        }
        try {
            setLoading(true);
            const payload = {
                isShowDeletedRecords: isActive,
                salesRecordBy: firstDropdown,
                expenseRecordBy: secondDropdown,
            };
            await axios.put(`${API_ENDPOINTS.UPDATE_SETTINGS}/${settingsId}`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchSettings();
            setSettings((prev) => ({
                ...prev,
                ...payload,
            }));
            setToast({ show: true, message: "Settings updated successfully", type: "success" });
        } catch (err) {
            console.error(err);
            setToast({ show: true, message: "Failed to update settings", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 bg-gray-50 h-full">
            <div
                className="bg-white rounded-2xl shadow-lg p-8 w-full flex flex-col justify-between"
            >
                <div>
                    <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
                        Settings
                    </h2>

                    <div className="space-y-6">
                        {/* Show Deleted Records */}
                        <div className="flex items-center gap-8">
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-gray-700">
                                    Show Deleted Records
                                </label>
                                <label className="relative inline-flex items-center cursor-pointer w-12 h-6">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={isActive}
                                        onChange={handleStatusToggle}
                                        disabled={loading}
                                    />
                                    <div className="w-12 h-6 bg-gray-300 peer-checked:bg-green-500 rounded-full transition-colors duration-300"></div>
                                    <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 transform peer-checked:translate-x-6" />
                                </label>
                            </div>
                        </div>

                        {/* Dropdowns */}
                        <div className="sm:flex gap-7">
                            <div className="flex-1 ">
                                <label className="block text-sm font-medium text-gray-700">
                                    Sales Record Sort By
                                </label>
                                <Select
                                    value={firstDropdown}
                                    placeholder="Select Option"
                                    onChange={(val) => setFirstDropdown(val)}
                                    className="w-full"
                                    disabled={loading}
                                >
                                    <Option value="saleDate">Sales Date</Option>
                                    <Option value="createdAt">Created At</Option>
                                </Select>
                            </div>

                            <div className="flex-1 mt-5 sm:mt-0">
                                <label className="block text-sm font-medium text-gray-700">
                                    Expense Record Sort By
                                </label>
                                <Select
                                    value={secondDropdown}
                                    placeholder="Select Option"
                                    onChange={(val) => setSecondDropdown(val)}
                                    className="w-full"
                                    disabled={loading}
                                >
                                    <Option value="expenseDate">Expense Date</Option>
                                    <Option value="createdAt">Created At</Option>
                                </Select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end mt-10">
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition disabled:opacity-50"
                    >
                        {loading ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>

            {/* Toast */}
            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast({ ...toast, show: false })}
                />
            )}

            <StripeProductTrackingPanel api={api} />
        </div>
    );
}
